import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, ChevronLeft, ChevronRight, ChevronDown,
  ScrollText, CheckCircle2, XCircle, Clock, Filter, RotateCcw, Globe, Radar,
  Trash2, AlertTriangle, Cpu,
} from "lucide-react";
import { adminAxios } from "./adminApi";
import PlatformIcon from "@/components/PlatformIcon";

const PAGE_SIZE = 50;

const STATUS_OPTIONS = ["", "completed", "failed", "refunded", "running"];
const FEATURE_OPTIONS = ["", "find_customers", "pain_points", "market", "partners"];
const KIND_OPTIONS = [
  { v: "", label: "All activity" },
  { v: "customer_scan", label: "Customer scans" },
  { v: "website_import", label: "Website imports" },
];

const FEATURE_LABEL = {
  find_customers: "Find customers",
  pain_points: "Pain points",
  market: "Market",
  partners: "Partners",
};

// Friendly names for the drop_report keys the pipeline writes.
const DROP_LABEL = {
  care_handle: "Support/care handle",
  deal_aggregator: "Deal aggregator",
  mlm: "MLM / spam",
  job_posting: "Job posting",
  own_brand_echo: "Own-brand echo",
  empty_shell: "Empty / thin",
  contact_spam: "Contact spam",
  nsfw_toxic: "NSFW / toxic",
  non_target_language: "Wrong language",
  age_floor: "Too old",
  dupe_url: "Duplicate URL",
  dupe_content: "Duplicate text",
  seller: "Seller / promoter",
  geo: "Out of market",
  off_intent: "Not a buyer (non-buying intent)",
  geo_or_seller: "Out of market / seller",
};

function StatusBadge({ status }) {
  const map = {
    completed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    failed: { cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
    refunded: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: RotateCcw },
    running: { cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: Clock },
  };
  const { cls, Icon } = map[status] || { cls: "bg-lm-tint text-lm-text2 border-lm-border", Icon: Clock };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>
      <Icon className="w-3 h-3" /> {status || "—"}
    </span>
  );
}

function KindBadge({ kind }) {
  const isImport = kind === "website_import";
  const Icon = isImport ? Globe : Radar;
  const cls = isImport
    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
    : "bg-lm-tint2 text-lm-primary border-lm-primary/20";
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-3 h-3" /> {isImport ? "Import" : "Scan"}
    </span>
  );
}

function fmtDuration(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

function DropReport({ report, total }) {
  const entries = Object.entries(report || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) {
    return <div className="text-xs text-lm-text3">No junk dropped on this scan.</div>;
  }
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-2">
        Junk filtered ({total} posts)
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="text-xs text-lm-text2 w-36 shrink-0 truncate">{DROP_LABEL[k] || k}</span>
            <div className="flex-1 h-2 rounded-full bg-lm-tint overflow-hidden">
              <div className="h-full rounded-full bg-lm-secondary/70" style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="text-xs font-mono text-lm-text w-8 text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScanRow({ s, onDelete }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isImport = s.kind === "website_import";

  const doDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    await onDelete(s.scan_id);
    // component unmounts on reload; no state reset needed
  };
  return (
    <>
      <tr
        data-testid={`scan-row-${s.scan_id}`}
        className="border-t border-lm-border/60 hover:bg-lm-tint/30 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-4 py-2.5 font-mono text-xs text-lm-text3 whitespace-nowrap">{fmtWhen(s.when)}</td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <KindBadge kind={s.kind} />
            <span className="text-xs text-lm-text2">{s.label}</span>
          </div>
        </td>
        <td className="px-4 py-2.5">
          <div className="text-sm font-medium truncate max-w-[180px]">{s.brand_name || "—"}</div>
          <div className="text-[10px] font-mono text-lm-text3 truncate max-w-[180px]">
            {s.workspace_name}{s.business_category ? ` · ${s.business_category}` : ""}
          </div>
        </td>
        <td className="px-4 py-2.5">
          <div className="text-sm text-lm-text truncate max-w-[180px]">{s.user_name || "—"}</div>
          <div className="text-[10px] font-mono text-lm-text3 truncate max-w-[180px]">{s.user_email || "—"}</div>
        </td>
        <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
        <td className="px-4 py-2.5">
          {isImport ? (
            <span className="text-[10px] font-mono text-lm-text3">—</span>
          ) : (
            <div className="flex items-center gap-1.5">
              {(s.platforms_called || []).map((p) => (
                <span key={p} title={p} className="inline-flex">
                  <PlatformIcon platform={p} className="w-4 h-4" />
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="px-4 py-2.5 text-right font-mono text-xs">
          {isImport ? <span className="text-lm-text3">—</span> : (
            <>
              <span className="text-lm-text3">{s.fetched_count ?? 0}</span>
              <span className="text-lm-text3 mx-1">→</span>
              <span className="text-emerald-700 font-semibold">{s.new_count ?? 0}</span>
            </>
          )}
        </td>
        <td className="px-4 py-2.5 text-right font-mono text-xs text-lm-secondary">{isImport ? "—" : (s.dropped_total ?? 0)}</td>
        <td className="px-4 py-2.5 text-right font-mono text-xs text-lm-text2 whitespace-nowrap">{fmtDuration(s.duration_seconds)}</td>
        <td className="px-2 py-2.5 text-lm-text3">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </td>
      </tr>
      {open && (
        <tr className="bg-lm-bg/60">
          <td colSpan={10} className="px-4 py-4">
            <div className="grid gap-4 md:grid-cols-[1.4fr,1fr]">
              {isImport ? (
                <div className="text-sm text-lm-text2 space-y-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1">Website import</div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-indigo-500" />
                    {s.website_url ? (
                      <a href={s.website_url} target="_blank" rel="noreferrer" className="text-lm-primary underline break-all">{s.website_url}</a>
                    ) : <span className="text-lm-text3">No website on file</span>}
                  </div>
                  <div className="text-xs text-lm-text3">
                    This run generated the brand profile, category, fit score and buyer keywords from the site.
                  </div>
                </div>
              ) : (
                <DropReport report={s.drop_report} total={s.dropped_total} />
              )}
              <div className="text-xs text-lm-text2 space-y-1.5 md:border-l md:border-lm-border md:pl-4">
                <div><span className="text-lm-text3">Ref:</span> <span className="font-mono">{s.scan_id}</span></div>
                <div><span className="text-lm-text3">User:</span> {s.user_name} · <span className="font-mono">{s.user_id || "—"}</span></div>
                <div><span className="text-lm-text3">Email:</span> {s.user_email}</div>
                <div><span className="text-lm-text3">Workspace:</span> <span className="font-mono">{s.workspace_id}</span></div>
                <div><span className="text-lm-text3">Plan:</span> {s.plan || "—"}</div>
                {!isImport && (
                  <div><span className="text-lm-text3">Fetched:</span> {s.fetched_count ?? 0} · <span className="text-lm-text3">New:</span> {s.new_count ?? 0} · <span className="text-lm-text3">Skipped:</span> {s.skipped_count ?? 0}</div>
                )}
                <div><span className="text-lm-text3">Credits:</span> −{s.credits_deducted ?? 0}{s.credits_refunded ? ` (refunded ${s.credits_refunded})` : ""}</div>
                {s.llm_vendor && (
                  <div className="flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5 text-lm-primary" /><span className="text-lm-text3">AI:</span> <span className="font-semibold uppercase">{s.llm_vendor}</span> <span className="text-lm-text3">· {s.llm_model}</span></div>
                )}
                <div><span className="text-lm-text3">When:</span> {fmtWhen(s.when)}</div>
                <div className="pt-2">
                  {confirming ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-xs text-red-700">Delete this log?</span>
                      <button onClick={doDelete} disabled={deleting} className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1">
                        {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Yes, delete
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setConfirming(false); }} className="text-xs px-2 py-1 rounded border border-lm-border hover:bg-lm-tint">Cancel</button>
                    </span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); setConfirming(true); }} className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete this log
                    </button>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatChip({ label, value, tone = "default" }) {
  const tones = {
    default: "border-lm-border bg-lm-surface text-lm-text",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    red: "border-red-200 bg-red-50 text-red-700",
    coral: "border-lm-secondary/25 bg-lm-secondary/10 text-lm-secondary",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-display font-semibold mt-0.5">{value}</div>
    </div>
  );
}

export default function AdminScans() {
  const [data, setData] = useState({ summary: {}, rows: [], total: 0, limit: PAGE_SIZE, offset: 0 });
  const [status, setStatus] = useState("");
  const [feature, setFeature] = useState("");
  const [kind, setKind] = useState("");
  const [wsq, setWsq] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get("/scans", {
        params: {
          status: status || undefined,
          feature: feature || undefined,
          kind: kind || undefined,
          workspace_id: wsq || undefined,
          limit: PAGE_SIZE,
          offset,
        },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load scan logs");
    } finally {
      setLoading(false);
    }
  }, [status, feature, kind, wsq, offset]);

  useEffect(() => { load(); }, [load]);

  const deleteScan = async (scanId) => {
    try {
      await adminAxios().delete(`/scans/${encodeURIComponent(scanId)}`);
      toast.success("Log deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteAll = async () => {
    setPurging(true);
    try {
      const r = await adminAxios().delete("/scans");
      const d = r.data || {};
      toast.success(`Cleared ${(d.deleted_scans || 0) + (d.deleted_imports || 0)} log rows`);
      setConfirmAll(false);
      setOffset(0);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete-all failed");
    } finally {
      setPurging(false);
    }
  };

  const sum = data.summary || {};
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div data-testid="admin-scans">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Observability</div>
          <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Scan logs</h1>
          <p className="text-sm text-lm-text2 mt-1">
            Every customer scan and website import across all workspaces — who ran it, status, platforms, leads vs junk, and timing. {data.total} total.
          </p>
          {data.llm && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full border border-lm-primary/25 bg-lm-tint2/50 text-lm-primary" title="The AI provider currently serving all analysis + brand-import calls">
              <Cpu className="w-3.5 h-3.5" />
              AI vendor: <span className="font-semibold uppercase">{data.llm.vendor}</span>
              <span className="text-lm-text3">· {data.llm.model}</span>
            </div>
          )}
        </div>
        {confirmAll ? (
          <div className="shrink-0 flex flex-col items-end gap-1.5 rounded-lg border border-red-200 bg-red-50 p-3 max-w-xs">
            <div className="flex items-center gap-1.5 text-xs text-red-700 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" /> Delete ALL {data.total} log rows?
            </div>
            <div className="text-[11px] text-red-600/80 text-right">Removes scan + import telemetry. Leads and users are kept.</div>
            <div className="flex gap-2 mt-1">
              <button onClick={deleteAll} disabled={purging} className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5">
                {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete all
              </button>
              <button onClick={() => setConfirmAll(false)} className="text-xs px-3 py-1.5 rounded-md border border-lm-border bg-lm-surface hover:bg-lm-tint">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmAll(true)}
            disabled={data.total === 0}
            data-testid="scans-delete-all"
            className="shrink-0 text-xs px-3 py-2 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete all logs
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-lm-surface border border-lm-border rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-text3" />
          <input
            data-testid="scans-search"
            value={wsq}
            onChange={(e) => setWsq(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setOffset(0), load())}
            placeholder="Filter by workspace id…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-lm-border bg-lm-bg text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20"
          />
        </div>
        <div className="inline-flex items-center gap-1.5 text-lm-text3">
          <Filter className="w-4 h-4" />
        </div>
        <select
          data-testid="scans-kind-filter"
          value={kind}
          onChange={(e) => { setKind(e.target.value); setOffset(0); }}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          {KIND_OPTIONS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
        <select
          data-testid="scans-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
        <select
          data-testid="scans-feature-filter"
          value={feature}
          onChange={(e) => { setFeature(e.target.value); setOffset(0); }}
          className="text-sm border border-lm-border rounded-md px-3 py-2 bg-lm-bg"
        >
          {FEATURE_OPTIONS.map((f) => <option key={f} value={f}>{f ? (FEATURE_LABEL[f] || f) : "All lenses"}</option>)}
        </select>
        <button onClick={() => (setOffset(0), load())} data-testid="scans-refresh" className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-2 hover:bg-lm-tint2">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Summary chips (across the filtered set) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <StatChip label="Total" value={sum.scans ?? 0} />
        <StatChip label="Customer scans" value={sum.customer_scans ?? 0} />
        <StatChip label="Website imports" value={sum.website_imports ?? 0} tone="indigo" />
        <StatChip label="Leads found" value={sum.new_leads ?? 0} tone="green" />
        <StatChip label="Junk filtered" value={sum.junk_dropped ?? 0} tone="coral" />
        <StatChip label="Refunded" value={sum.refunded ?? 0} tone="red" />
      </div>

      <div className="bg-lm-surface border border-lm-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lm-tint/60 border-b border-lm-border text-lm-text3">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">When</th>
                <th className="text-left px-4 py-2.5 font-medium">Type</th>
                <th className="text-left px-4 py-2.5 font-medium">Brand / workspace</th>
                <th className="text-left px-4 py-2.5 font-medium">User</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 font-medium">Platforms</th>
                <th className="text-right px-4 py-2.5 font-medium">Fetched → New</th>
                <th className="text-right px-4 py-2.5 font-medium">Junk</th>
                <th className="text-right px-4 py-2.5 font-medium">Time</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading scan logs…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-lm-text3"><ScrollText className="w-6 h-6 inline mb-2 text-lm-text3" /><div>No activity matches these filters.</div></td></tr>
              ) : data.rows.map((s) => <ScanRow key={s.scan_id} s={s} onDelete={deleteScan} />)}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-lm-border text-xs text-lm-text3 font-mono">
          <div>Page {page} of {totalPages} · {data.total} rows</div>
          <div className="flex items-center gap-1">
            <button
              data-testid="scans-prev"
              disabled={offset <= 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="scans-next"
              disabled={page >= totalPages}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <p className="mt-3 text-xs text-lm-text3">Tip: click any row to expand — junk-filter breakdown for scans, imported website for imports, plus full user + credit detail.</p>
    </div>
  );
}
